import Errors from "../../utilities/constants/Errors";
import KeycloakService from "../config/KeycloakService";
import HttpClient from "../../utilities/functions/HttpClient";
import CustomToast from "../../utilities/components/CustomToast";
import {
  RESERVATIONS_AVAILABILITY_ENDPOINT,
  RESERVATIONS_ENDPOINT,
  RESERVATIONS_ESTIMATE_ENDPOINT,
  RESERVATION_IDEMPOTENCY_HEADER_ENABLED,
  USERS_ENDPOINT,
} from "../../utilities/constants/ServerEndPoints";

const request = async (operation, { setIsLoading, setApiError } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleReservationRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeReservationQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

const createReservationIdempotencyKey = () =>
  `reservation-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const buildReservationCreateConfig = () => {
  if (!RESERVATION_IDEMPOTENCY_HEADER_ENABLED) {
    return undefined;
  }

  return {
    headers: {
      "Idempotency-Key": createReservationIdempotencyKey(),
    },
  };
};

export const mapRequestedAddOnsForApi = (requestedAddOns = []) =>
  requestedAddOns
    .filter((item) => item?.addOn?.id != null && Number(item?.quantity) > 0)
    .map((item) => ({
      id: item.id ?? null,
      addOn: item.addOn,
      quantity: Number(item.quantity),
    }));

export const normalizeReservationDateTime = (value) => {
  if (!value) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? value : parsedDate.toISOString();
};

export const buildReservationEstimatePayload = (reservation) => ({
  startingDateTime: normalizeReservationDateTime(reservation.startingDateTime),
  endingDateTime: normalizeReservationDateTime(reservation.endingDateTime),
  numberOfSeats: Number(reservation.numberOfSeats || 0),
  pricingStrategy: reservation.pricingStrategy || "FULL_DAY",
  addOns: mapRequestedAddOnsForApi(reservation.addOns),
  addOnsTotalCost: Number(reservation.addOnsTotalCost || 0),
  taxRate: Number(reservation.taxRate || 0),
});

export const buildReservationAvailabilityPayload = (reservation) => ({
  startingDateTime: normalizeReservationDateTime(reservation.startingDateTime),
  endingDateTime: normalizeReservationDateTime(reservation.endingDateTime),
  effectiveEndingDateTime:
    normalizeReservationDateTime(reservation.effectiveEndingDateTime) || null,
  reservationId: reservation.reservationId ?? reservation.id ?? null,
});

export const buildReservationRequestPayload = (reservation) => ({
  id: reservation.id ?? null,
  startingDateTime: normalizeReservationDateTime(reservation.startingDateTime),
  endingDateTime: normalizeReservationDateTime(reservation.endingDateTime),
  effectiveEndingDateTime:
    normalizeReservationDateTime(reservation.effectiveEndingDateTime) || null,
  eventType: reservation.eventType,
  numberOfSeats: Number(reservation.numberOfSeats || 0),
  addOns: mapRequestedAddOnsForApi(reservation.addOns),
  addOnsTotalCost: Number(reservation.addOnsTotalCost || 0),
  status: reservation.status,
  fullPackage: Boolean(reservation.fullPackage),
  securityDepositRefunded: Boolean(reservation.securityDepositRefunded),
  taxRate: Number(reservation.taxRate || 0),
  totalPrice: Number(reservation.totalPrice || 0),
  priceComputationMethod: reservation.priceComputationMethod,
  pricingStrategy: reservation.pricingStrategy || "FULL_DAY",
  userId: reservation.userId,
});

/**
 * Fetch all reservations present in the database.
 * @param {function} setReservationsData The function to collect the data provided by the backend.
 */
export const getAllReservations = async (setReservationsData, params) => {
  const normalizedParams = normalizeReservationQueryParams(params);
  const response = await request(() =>
    HttpClient.getAxiosClient().get(`${RESERVATIONS_ENDPOINT}`, { params: normalizedParams }),
  );

  if (response.status === 200) {
    setReservationsData(response.data);
  }
};

/**
 * Retrieve a single reservation from the database.
 * @param {Function} setReservationData The function to collect the fetched data.
 * @param {number} id The appointment's id to fetch.
 */
export const getReservationById = async (setReservationData, id) => {
  const userId = KeycloakService.getUserId();
  const response = await request(() =>
    HttpClient.getAxiosClient().get(`${USERS_ENDPOINT}/${userId}${RESERVATIONS_ENDPOINT}/${id}`),
  );

  if (response.status === 200) {
    setReservationData(response.data);
  }
};

/**
 * This function is used to retrieve all the reservations linked to a specific user.
 * @param {String} id The user's id to whose the reservations are linked.
 * @param {Object} params The parameters to filter the data.
 * @param {Function} setReservationData The function to collect the fetched data.
 * @param {Function} setApiError The function to update the api error status.
 * @param {Function} setIsLoading The function to update the loading status.
 */
export const getReservationByUserId = async (
  id,
  params,
  setReservationData,
  setApiError,
  setIsLoading,
) => {
  const normalizedParams = normalizeReservationQueryParams(params);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(`${USERS_ENDPOINT}/${id}${RESERVATIONS_ENDPOINT}`, {
        params: normalizedParams,
      }),
    { setApiError, setIsLoading },
  );

  if (response.status === 200) {
    setReservationData(response.data);
  }
};

/**
 * Fetch a server-side reservation price estimate.
 */
export const estimateReservationPrice = async (
  reservation,
  setApiError,
  setIsLoading,
) => {
  const payload = buildReservationEstimatePayload(reservation);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().post(RESERVATIONS_ESTIMATE_ENDPOINT, payload),
    { setApiError, setIsLoading },
  );

  return response.data;
};

/**
 * Check whether a reservation time frame is currently available.
 * This helper is intentionally silent (no toast spam) so callers can show inline UI feedback.
 */
export const checkReservationAvailability = async (reservation) => {
  const payload = buildReservationAvailabilityPayload(reservation);

  try {
    const response = await HttpClient.getAxiosClient().post(
      RESERVATIONS_AVAILABILITY_ENDPOINT,
      payload,
    );

    return {
      available: true,
      message: response.data?.message || null,
    };
  } catch (err) {
    const status = getErrorStatus(err);
    const backendMessage = getBackendErrorMessage(err);

    if (status === 409) {
      return {
        available: false,
        message: backendMessage || Errors.CONFLICTING_TIME_FRAME_ERROR,
      };
    }

    if (status === 400) {
      return {
        available: false,
        message: backendMessage || "Please review the selected date and time.",
      };
    }

    return {
      available: null,
      message: null,
    };
  }
};

/**
 * Cancel an single reservation.
 * @param {number} id The targeted addOn identifier.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 */
export const cancelReservation = async (
  id,
  setState,
  setIsLoading,
  setApiError,
) => {
  const userId = KeycloakService.getUserId();
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${USERS_ENDPOINT}/${userId}${RESERVATIONS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading },
  );

  if (response.status === 204) {
    CustomToast("Reservation successfully cancelled", "success");
    setState?.((prev) => !prev);
  }
};

/**
 * Cancel multiple reservations at once.
 * @param {Array} ids A list of identifiers.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 */
export const cancelReservations = async (
  ids,
  setState,
  setIsLoading,
  setApiError,
) => {
  setApiError?.(true);
  try {
    throwUnsupportedCustomerOperation(
      "Bulk reservation cancellation is an admin-only operation and is not available from the customer portal.",
    );
  } finally {
    setIsLoading?.(false);
  }
};

/**
 * Update an reservation in the database.
 * @param {Object} reservation The updated information of the addOn the targeted addOn.
 * @param {Function} setApiError The function to update the api error status.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 */
export const updateReservation = async (
  reservation,
  setApiError,
  setState,
  setIsLoading,
) => {
  const userId = KeycloakService.getUserId();
  const payload = buildReservationRequestPayload(reservation);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().put(
        `${USERS_ENDPOINT}/${userId}${RESERVATIONS_ENDPOINT}/${reservation.id}`,
        payload,
      ),
    { setApiError, setIsLoading },
  );

  if (response.status === 200) {
    CustomToast("Reservation update successfully requested.", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

/**
 * Update an reservation in the database.
 * @param {Object} reservation The updated information of the targeted reservation.
 * @param {Function} setApiError The function to update the api error status.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 */
export const createReservation = async (
  reservation,
  setApiError,
  setState,
  setIsLoading,
) => {
  const payload = buildReservationRequestPayload(reservation);
  const requestConfig = buildReservationCreateConfig();
  const response = await request(
    () =>
      HttpClient.getAxiosClient().post(
        `${RESERVATIONS_ENDPOINT}`,
        payload,
        requestConfig,
      ),
    { setApiError, setIsLoading },
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Reservation request submitted successfully.", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleReservationRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || Errors.NOT_FOUND_ERROR, "info");
      return;
    case 409:
      CustomToast(backendMessage || Errors.CONFLICTING_TIME_FRAME_ERROR, "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.code === "ECONNABORTED") {
    CustomToast(Errors.REQUEST_TIMEOUT_ERROR, "error");
    return;
  }

  if (err?.response == null) {
    CustomToast(Errors.NETWORK_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};

const throwUnsupportedCustomerOperation = (message) => {
  CustomToast(message, "error");
  throw new Error(message);
};
